import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';

interface ImageSettings {
  tenantId: string;
  hasApiKey: boolean;
  source: 'tenant' | 'env' | null;
  configured: boolean;
  defaultType: 'TEXTTOIAMGE' | 'IMAGETOIAMGE';
  updatedAt: string;
}

/**
 * Settings → AI Images. Each tenant stores its OWN Nano Banana API key
 * (encrypted at rest); when unset the platform default key is used as a
 * fallback. Mirrors the AI assistant's key settings. Gated by imagegen.admin.
 */
export function ImageSettingsPage(): JSX.Element {
  const { t } = useTranslation('imagegen');
  const [settings, setSettings] = useState<ImageSettings | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [defaultType, setDefaultType] = useState<'TEXTTOIAMGE' | 'IMAGETOIAMGE'>('TEXTTOIAMGE');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = (): void => {
    void api()
      .get<ImageSettings>('/imagegen/settings')
      .then((r) => {
        setSettings(r.data);
        setDefaultType(r.data.defaultType);
      })
      .catch((e) => setError((e as Error).message));
  };
  useEffect(load, []);

  const onSave = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const body: Record<string, unknown> = { defaultType };
      if (apiKey.trim()) body.apiKey = apiKey.trim();
      const r = await api().put<ImageSettings>('/imagegen/settings', body);
      setSettings(r.data);
      setApiKey('');
      setSaved(true);
    } catch (err) {
      setError(String((err as Error).message ?? err));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const statusLabel = settings.hasApiKey
    ? t('settings.keySet', { defaultValue: 'Tenant key set' })
    : settings.source === 'env'
      ? t('settings.usingEnv', { defaultValue: 'Using the platform default key' })
      : t('settings.keyMissing', { defaultValue: 'No key configured' });
  const statusClass = settings.configured ? 'text-success' : 'text-danger';

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold">{t('settings.title', { defaultValue: 'AI Images' })}</h2>
      <p className="mt-1 text-sm text-fg-muted">
        {t('settings.subtitle', {
          defaultValue: "Connect this organization's Nano Banana API key. Each tenant uses its own key.",
        })}
      </p>

      <form onSubmit={onSave} className="mt-6 flex max-w-xl flex-col gap-4" noValidate>
        <FormField
          id="ig-key"
          label={
            <span className="flex items-center gap-2">
              {t('settings.apiKey', { defaultValue: 'Nano Banana API key' })}
              <span className={`text-xs font-medium ${statusClass}`}>· {statusLabel}</span>
            </span>
          }
        >
          <Input
            id="ig-key"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={settings.hasApiKey ? '••••••••••' : t('settings.placeholderEmpty', { defaultValue: 'paste API key here' })}
          />
          <p className="mt-1 text-xs text-fg-muted">
            {t('settings.apiKeyHint', {
              defaultValue: 'Get a key at nanobananaapi.ai. Stored encrypted; never shown again.',
            })}
          </p>
        </FormField>

        <FormField id="ig-default-type" label={t('settings.defaultType', { defaultValue: 'Default mode' })}>
          <select
            id="ig-default-type"
            className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
            value={defaultType}
            onChange={(e) => setDefaultType(e.target.value as 'TEXTTOIAMGE' | 'IMAGETOIAMGE')}
          >
            <option value="TEXTTOIAMGE">{t('studio.typeText', { defaultValue: 'Text → image' })}</option>
            <option value="IMAGETOIAMGE">{t('studio.typeImage', { defaultValue: 'Image → image (edit)' })}</option>
          </select>
        </FormField>

        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        {saved && (
          <p className="text-sm text-success" role="status">
            {t('settings.saved', { defaultValue: 'Settings saved.' })}
          </p>
        )}

        <div>
          <Button type="submit" loading={saving}>
            {t('settings.save', { defaultValue: 'Save' })}
          </Button>
        </div>
      </form>
    </Card>
  );
}
